import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { ZodError } from 'zod';

import { PageLoading } from '@repo/features/page-loading';
import { PageNotFound } from '@repo/features/page-not-found';
import { trackPageView } from '@repo/features/telemetry';
import { parseHttpError } from '@repo/utils/parse-http-error';

import { PageError } from '~/features/page-error/page-error.tsx';
import { routeTree } from '~/routeTree.gen';
import { NotFoundError } from '~/services/http.ts';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        return (
          count < 4
          && !(error instanceof ZodError)
          && !(error instanceof NotFoundError)
          && parseHttpError(error).status !== 401
        );
      },
    },
  },
});

export const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultNotFoundComponent: PageNotFound,
  defaultErrorComponent: () => <PageError msg="An error occurred while loading the necessary data for this view." />,
  defaultPendingComponent: PageLoading,
  defaultPendingMs: 200,

  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  scrollToTopSelectors: ['[data-scroll-area-viewport="base-layout"]'],
});

router.subscribe('onResolved', ({ toLocation, pathChanged }) => {
  if (pathChanged) {
    trackPageView(toLocation.pathname, toLocation.href);
  }
});

declare module '@tanstack/react-router' {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Register {
    router: typeof router;
  }
}
